import { z } from 'zod';
import type { DuplicateReason } from './models';

/**
 * Duplicate review.
 *
 * Pairs that score below the auto-merge threshold are queued here instead of being merged
 * silently: a false merge destroys data that is hard to recover, so the decision stays with a
 * person, with the evidence for the match shown side by side.
 */
export interface DuplicateAccountSummary {
  id: string;
  name: string;
  category: string | null;
  address: string | null;
  area: string | null;
  city: string | null;
  phone: string | null;
  email: string | null;
  /** Every phone/email on the record. The match reasons may cite a shared secondary value, so
   *  the reviewer has to see all of them — not just the primary — to judge the pair. */
  phones: string[];
  emails: string[];
  website: string | null;
  domain: string | null;
  sources: string[];
  sourceCount: number;
  peopleCount: number;
  createdAt: number;
}

export interface DuplicateCandidateDto {
  id: string;
  projectId: string;
  /** 0..1 similarity; the run's review threshold decides what lands here. */
  score: number;
  reasons: DuplicateReason[];
  status: 'pending' | 'merged' | 'dismissed';
  createdAt: number;
  resolvedAt: number | null;
  a: DuplicateAccountSummary;
  b: DuplicateAccountSummary;
}

export const DuplicateQuerySchema = z.object({
  projectId: z.string().max(64).optional(),
  status: z.enum(['pending', 'merged', 'dismissed', 'all']).default('pending'),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export const ResolveDuplicateSchema = z.object({
  action: z.enum(['merge', 'dismiss']),
  /** The account that survives a merge. Defaults to the better-sourced of the pair. */
  primaryId: z.string().max(64).optional(),
});
export type ResolveDuplicateRequest = z.input<typeof ResolveDuplicateSchema>;

/** Fields worth comparing side by side when reviewing a pair. */
export const COMPARED_FIELDS: ReadonlyArray<{ key: keyof DuplicateAccountSummary; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'category', label: 'Category' },
  { key: 'phone', label: 'Phone' },
  { key: 'email', label: 'Email' },
  { key: 'domain', label: 'Domain' },
  { key: 'address', label: 'Address' },
  { key: 'area', label: 'District' },
  { key: 'city', label: 'City' },
];
